import React, { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { toast } from "sonner";

const allDurations = ["1 uur", "Halve dag", "Hele dag", "Avond", "Meerdere dagen"];
const meetingDurations = ["Halve dag", "Hele dag"];

export default function BookingForm({ spaceSlug, spaceName, hideSchedule = false, meetingMode = false }) {
  const durations = meetingMode ? meetingDurations : allDurations;
  const [date, setDate] = useState();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    start_time: "",
    duration: meetingMode ? "Halve dag" : "Halve dag",
    attendees: 1,
    message: "",
    company_name: "",
    vat_number: "",
    billing_address: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!hideSchedule && !date) {
      toast.error("Kies eerst een datum");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("Naam en e-mail zijn verplicht");
      return;
    }
    if (meetingMode && (!form.company_name || !form.billing_address)) {
      toast.error("Vul je bedrijfsnaam en factuuradres in voor de facturatie");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        space_slug: spaceSlug,
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: date ? format(date, "yyyy-MM-dd") : "",
        start_time: form.start_time,
        duration: hideSchedule ? "" : form.duration,
        attendees: Number(form.attendees) || 1,
        message: form.message,
        company_name: form.company_name,
        vat_number: form.vat_number,
        billing_address: form.billing_address,
      });
      setSubmitted(true);
      toast.success(
        meetingMode
          ? "Boeking ontvangen. We bevestigen je reservatie en sturen de factuur op."
          : "Aanvraag ontvangen. We nemen snel contact met je op."
      );
    } catch (err) {
      toast.error("Kon je aanvraag niet versturen. Probeer het opnieuw.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="card-flat p-10" data-testid="booking-success-message">
        <p className="overline" style={{ color: "var(--accent)" }}>Aanvraag ontvangen</p>
        <h3 className="font-serif text-3xl mt-3 mb-4 font-light leading-tight">
          Dankjewel, {form.name.split(" ")[0]}.
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          We hebben je aanvraag voor{" "}
          <span style={{ color: "var(--text-primary)" }}>{spaceName}</span>
          {date ? (
            <>
              {" "}op{" "}
              <span style={{ color: "var(--text-primary)" }}>{format(date, "EEEE d MMMM yyyy")}</span>
            </>
          ) : null}{" "}
          goed ontvangen. We nemen snel contact op via{" "}
          <span style={{ color: "var(--text-primary)" }}>{form.email}</span> met de beschikbaarheid en
          een aanbod op maat.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="card-flat p-8 md:p-10"
      data-testid={`booking-form-${spaceSlug}`}
    >
      <p className="overline" style={{ color: "var(--accent)" }}>
        {meetingMode ? "Boeking" : "Aanvraag"}
      </p>
      <h3 className="font-serif text-3xl md:text-4xl font-light mt-3 leading-tight">
        {meetingMode ? `Boek ${spaceName}` : `Vraag ${spaceName} aan`}
      </h3>
      <div className="divider mt-6 mb-8" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
            Naam
          </label>
          <input
            data-testid="booking-name-input"
            className="input-bare"
            placeholder="Anna De Coster"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            required
          />
        </div>

        <div>
          <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
            E-mail
          </label>
          <input
            data-testid="booking-email-input"
            type="email"
            className="input-bare"
            placeholder="anna@studio.be"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            required
          />
        </div>

        <div>
          <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
            Telefoon
          </label>
          <input
            data-testid="booking-phone-input"
            className="input-bare"
            placeholder="+32 …"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
        </div>

        <div>
          <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
            Aantal personen
          </label>
          <input
            data-testid="booking-attendees-input"
            type="number"
            min={1}
            className="input-bare"
            value={form.attendees}
            onChange={(e) => update("attendees", e.target.value)}
          />
        </div>

        {!hideSchedule && (
          <>
            <div>
              <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                Gewenste datum
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="booking-date-trigger"
                    className="input-bare text-left flex items-center justify-between"
                  >
                    <span style={{ color: date ? "var(--text-primary)" : "#a59c8e" }}>
                      {date ? format(date, "EEEE d MMMM yyyy") : "Kies een datum"}
                    </span>
                    <CalendarDays size={16} style={{ color: "var(--text-secondary)" }} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                    data-testid="booking-date-calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                Duur
              </label>
              <select
                data-testid="booking-duration-select"
                className="input-bare"
                value={form.duration}
                onChange={(e) => update("duration", e.target.value)}
              >
                {durations.map((d) => (
                  <option key={d}>{d}</option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="md:col-span-2">
          <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
            {meetingMode
              ? "Bijkomende info"
              : hideSchedule
              ? "Vertel ons kort wat je zoekt"
              : "Wat heb je in gedachten?"}
          </label>
          <textarea
            data-testid="booking-message-input"
            className="input-bare"
            placeholder={meetingMode
              ? "Bijv. opstelling, catering, gewenste startuur…"
              : hideSchedule
              ? "Aantal personen, gewenste startdatum, type werk…"
              : "Vertel ons kort iets over het event, de shoot of de meeting…"}
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
          />
        </div>

        {meetingMode && (
          <>
            <div className="md:col-span-2 mt-2">
              <p className="overline" style={{ color: "var(--accent)" }}>Facturatie</p>
              <div className="divider mt-3 mb-2" />
            </div>
            <div>
              <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                Bedrijfsnaam
              </label>
              <input
                data-testid="booking-company-input"
                className="input-bare"
                placeholder="Atelier De Coster bv"
                value={form.company_name}
                onChange={(e) => update("company_name", e.target.value)}
                required
              />
            </div>
            <div>
              <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                BTW-nummer
              </label>
              <input
                data-testid="booking-vat-input"
                className="input-bare"
                placeholder="BE 0000.000.000"
                value={form.vat_number}
                onChange={(e) => update("vat_number", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                Factuuradres
              </label>
              <textarea
                data-testid="booking-billing-address-input"
                className="input-bare"
                placeholder="Straat & nummer, postcode, gemeente"
                value={form.billing_address}
                onChange={(e) => update("billing_address", e.target.value)}
                required
              />
            </div>
          </>
        )}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <button
          type="submit"
          disabled={submitting}
          data-testid="booking-submit-btn"
          className="btn-primary"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
          {submitting ? "Versturen…" : (meetingMode ? "Boek nu" : "Verstuur aanvraag")}
        </button>
        <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
          {meetingMode
            ? "Betaling via factuur. We sturen de factuur op na bevestiging."
            : "Geen betaling nu. We bevestigen eerst beschikbaarheid en prijs."}
        </p>
      </div>
    </form>
  );
}
